import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Activity,
  CheckCircle,
  Clock,
  Filter,
  Search,
  ChevronRight,
  AlertCircle,
  Loader2,
  Server,
  FileWarning,
  Code2,
  Github,
  Calendar,
  XCircle,
  ShipWheel,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiClient, IncidentSummary, Cluster } from '../api/client';
import { ModeToggle } from '@/components/mode-toggle';
import { cn } from '@/lib/utils';
import { getStatusLabel } from '../lib/incidents';
import { format } from 'date-fns';
import { MobileMenuButton } from '@/components/MobileMenuButton';

// Format timestamp to relative time
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp * 1000;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ago`;
  } else if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return 'Just now';
  }
}

// Format timestamp to full datetime
function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Get severity icon and color
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'high':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'medium':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    case 'low':
      return <AlertCircle className="h-4 w-4 text-blue-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}

// Get severity badge variant
function getSeverityBadgeVariant(severity: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    default:
      return 'secondary';
  }
}

// Get status icon
function getStatusIcon(status: string) {
  switch (status) {
    case 'open':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'investigating':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'resolved':
      return <FileWarning className="h-4 w-4 text-amber-500" />;
    case 'remediation_applied':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'resolved_confirmed':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}

// Get status badge variant
function getStatusBadgeVariant(status: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (status) {
    case 'open':
      return 'destructive';
    case 'investigating':
    case 'analyzing_code':
      return 'default';
    case 'resolved':
      return 'outline';
    case 'remediation_applied':
      return 'secondary';
    case 'resolved_confirmed':
      return 'outline';
    default:
      return 'secondary';
  }
}

// Get incident type label
// Get initials from email
function getInitials(email: string): string {
  if (!email) return '?';
  const parts = email.split('@')[0].split('.');
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return email.split('@')[0].charAt(0).toUpperCase();
}

function getIncidentTypeLabel(type: string): string {
  switch (type) {
    // Infrastructure incidents
    case 'OOMKilled':
      return 'Out of Memory';
    case 'CrashLoopBackOff':
      return 'Crash Loop';
    case 'ImagePullError':
      return 'Image Pull Error';
    case 'RolloutFailure':
      return 'Rollout Failure';
    case 'NodeNotReady':
      return 'Node Not Ready';
    case 'MissingPVC':
      return 'Missing PVC';
    case 'InsufficientResources':
      return 'Insufficient Resources';
    case 'SchedulingFailure':
      return 'Scheduling Failure';
    case 'TaintTolerationMismatch':
      return 'Taint Mismatch';
    case 'NodeSelectorMismatch':
      return 'Node Selector Mismatch';
    case 'PodEvicted':
      return 'Pod Evicted';
    case 'LivenessProbeFailure':
      return 'Liveness Probe Failure';
    case 'ReadinessProbeFailure':
      return 'Readiness Probe Failure';
    case 'HealthCheckFailure':
      return 'Health Check Failure';
    case 'VolumeError':
      return 'Volume Error';
    case 'VolumeResizeError':
      return 'Volume Resize Error';
    case 'NetworkError':
      return 'Network Error';
    case 'RBACError':
      return 'RBAC Error';
    case 'ResourceExhaustion':
      return 'Resource Exhaustion';
    case 'ContainerRuntimeError':
      return 'Container Runtime Error';
    case 'LifecycleHookError':
      return 'Lifecycle Hook Error';
    case 'ConfigError':
      return 'Configuration Error';
    case 'PodFailure':
      return 'Pod Failure';
    case 'NodeIssue':
      return 'Node Issue';
    case 'Unknown':
      return 'Unknown Incident';
    case 'Failed':
      return 'Failed Incident';
    // Application incidents
    case 'ApplicationError':
      return 'Application Error';
    case 'Segfault':
      return 'Segmentation Fault';
    case 'NullPointerException':
      return 'Null Pointer';
    case 'Panic':
      return 'Panic';
    case 'UncaughtException':
      return 'Uncaught Exception';
    case 'IndexOutOfBounds':
      return 'Index Out of Bounds';
    case 'DivisionByZero':
      return 'Division by Zero';
    case 'MemoryError':
      return 'Memory Error';
    case 'StackOverflow':
      return 'Stack Overflow';
    case 'AssertionError':
      return 'Assertion Failed';
    case 'Deadlock':
      return 'Deadlock';
    default:
      return type;
  }
}

export default function IncidentResponse() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<IncidentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('resolved');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [hideTransient, setHideTransient] = useState<boolean>(true); // Hide transient incidents by default
  
  // Date range filtering - default to last 24 hours
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => {
    const lastDay = new Date();
    lastDay.setDate(lastDay.getDate() - 1);
    lastDay.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return { from: lastDay, to: today };
  });
  
  const [pageSize] = useState<number>(10);
  const [pageToken, setPageToken] = useState<string>('');
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageTokenHistory, setPageTokenHistory] = useState<string[]>([]); // Track page tokens for back navigation
  const [aggregateStats, setAggregateStats] = useState<{
    investigating: number;
    resolved: number;
    remediation_applied: number;
    resolved_confirmed: number;
    critical: number;
    high: number;
  }>({
    investigating: 0,
    resolved: 0,
    remediation_applied: 0,
    resolved_confirmed: 0,
    critical: 0,
    high: 0,
  });

  // Fetch clusters for filter dropdown
  useEffect(() => {
    async function fetchClusters() {
      try {
        const response = await apiClient.listClusters();
        setClusters(response.clusters);
      } catch (err: any) {
        console.error('Failed to fetch clusters:', err);
      }
    }

    fetchClusters();
  }, []);

  // Debounce search query for server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch incidents with server-side filtering and pagination
  useEffect(() => {
    async function fetchIncidents(showLoading = true) {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        // Convert date range to Unix timestamps for API
        const startTime = Math.floor(dateRange.from.getTime() / 1000);
        const endTime = Math.floor(dateRange.to.getTime() / 1000);

        // Fetch incidents with server-side filtering and pagination
        const response = await apiClient.listIncidents(
          selectedCluster === 'all' ? undefined : selectedCluster,
          selectedStatus === 'all' ? undefined : selectedStatus,
          pageSize,
          pageToken,
          startTime,
          endTime,
          hideTransient, // Pass hideTransient to server for server-side filtering
          debouncedSearchQuery || undefined  // Server-side search across all incidents
        );

        setIncidents(response.incidents || []);
        setTotalCount(response.total_count || 0);
        setNextPageToken(response.next_page_token || '');

        // Save aggregate statistics from server
        setAggregateStats({
          investigating: response.investigating_count || 0,
          resolved: response.resolved_count || 0,
          remediation_applied: response.remediation_applied_count || 0,
          resolved_confirmed: response.resolved_confirmed_count || 0,
          critical: response.critical_count || 0,
          high: response.high_count || 0,
        });
      } catch (err: any) {
        console.error('Failed to fetch incidents:', err);
        setError(err.response?.data?.message || 'Failed to load incidents');
      } finally {
        setLoading(false);
      }
    }

    fetchIncidents();

    // Auto-refresh every 30 seconds without showing loading spinner
    const interval = setInterval(() => {
      fetchIncidents(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedCluster, selectedStatus, pageToken, pageSize, dateRange, hideTransient, debouncedSearchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    setPageToken('');
    setCurrentPage(1);
    setPageTokenHistory([]); // Clear page token history
  }, [selectedCluster, selectedStatus, dateRange, hideTransient, debouncedSearchQuery]);

  const handleNextPage = () => {
    // Server-side pagination (search or not)
    if (nextPageToken) {
      setPageTokenHistory((prev) => [...prev, pageToken]);
      setPageToken(nextPageToken);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && pageTokenHistory.length > 0) {
      const newHistory = [...pageTokenHistory];
      const previousPageToken = newHistory.pop() || '';
      setPageTokenHistory(newHistory);
      setPageToken(previousPageToken);
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Client-side search filtering only (transient, date range, and status are server-side)
  // Server-side search is now used - incidents are already filtered by the API
  const paginatedIncidents = incidents; // Already paginated and filtered by server

  const totalFilteredCount = totalCount;
  const totalPages = Math.ceil(totalFilteredCount / pageSize);

  // Use aggregate stats from server (counts across all pages, including search-filtered)
  const stats = {
    total: totalCount,
    investigating: aggregateStats.investigating,
    resolved: aggregateStats.resolved,
    remediation_applied: aggregateStats.remediation_applied,
    resolved_confirmed: aggregateStats.resolved_confirmed,
    critical: aggregateStats.critical,
    high: aggregateStats.high,
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MobileMenuButton />
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              Kubernetes Incidents
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              AI-powered detection and analysis of Kubernetes application and infrastructure incidents.
            </p>
          </div>
        </div>
        <ModeToggle />
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Incidents</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Investigating</CardTitle>
            <Loader2 className={`h-4 w-4 text-muted-foreground ${stats.investigating > 0 ? 'animate-spin' : ''}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.investigating}</div>
            <p className="text-xs text-muted-foreground">
              AI analysis in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Analysis Complete</CardTitle>
            <FileWarning className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.resolved}</div>
            <p className="text-xs text-muted-foreground">
              Ready to apply
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.remediation_applied + stats.resolved_confirmed}</div>
            <p className="text-xs text-muted-foreground">
              Incident closed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search incidents by type, resource, or cluster..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[280px] justify-start text-left font-normal",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange?.from && dateRange?.to ? (
                    <>
                      {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                    </>
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={{ from: dateRange?.from, to: dateRange?.to }}
                  onSelect={(range: any) => {
                    if (range?.from && range?.to) {
                      const from = new Date(range.from);
                      from.setHours(0, 0, 0, 0);
                      const to = new Date(range.to);
                      to.setHours(23, 59, 59, 999);
                      setDateRange({ from, to });
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {/* Cluster Filter */}
            <Select value={selectedCluster} onValueChange={setSelectedCluster}>
              <SelectTrigger className="w-[220px]">
                <ShipWheel className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Cluster" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clusters</SelectItem>
                {clusters.map((cluster) => (
                  <SelectItem key={cluster.id} value={cluster.id}>
                    {cluster.cluster_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[200px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="resolved">Analysis Complete</SelectItem>
                <SelectItem value="remediation_applied">Remediation Applied</SelectItem>
                <SelectItem value="resolved_confirmed">Resolved</SelectItem>
              </SelectContent>
            </Select>

            {/* Hide Transient Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={hideTransient ? "secondary" : "outline"}
                    onClick={() => setHideTransient(!hideTransient)}
                    className="whitespace-nowrap"
                  >
                    {hideTransient ? (
                      <XCircle className="mr-2 h-4 w-4" />
                    ) : (
                      <Activity className="mr-2 h-4 w-4" />
                    )}
                    {hideTransient ? 'Hiding Transient' : 'Show All'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{hideTransient ? 'Click to show transient incidents' : 'Click to hide transient incidents'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Incidents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detected Incidents</CardTitle>
          <CardDescription>
            Kubernetes incidents detected and analyzed by AI agents
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : paginatedIncidents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Kubernetes incidents found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery || dateRange
                  ? 'No incidents match your search criteria. Try adjusting your filters or date range.'
                  : 'All systems are operating normally. Incidents will appear here when detected.'}
              </p>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Incident</TableHead>
                    <TableHead>Affected Resource</TableHead>
                    <TableHead>Cluster</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Detected</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedIncidents.map((incident) => (
                    <TableRow
                      key={incident.id}
                      className={`cursor-pointer hover:bg-muted/50 ${incident.is_cluster_group ? 'bg-orange-50/50 dark:bg-orange-950/20' : ''}`}
                      onClick={() => {
                        if (incident.is_cluster_group) {
                          navigate(`/incidents/cluster/${incident.cluster_id}`, {
                            state: {
                              clusterName: incident.cluster_name,
                              hideTransient,
                              dateRange: { from: dateRange.from.toISOString(), to: dateRange.to.toISOString() },
                              selectedStatus,
                              searchQuery: debouncedSearchQuery,
                            }
                          });
                        } else {
                          navigate(`/incidents/${incident.id}`);
                        }
                      }}
                    >
                      <TableCell>
                        {incident.is_cluster_group ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium">Cluster Resource Exhaustion</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={getSeverityBadgeVariant(incident.severity)}>
                                {incident.severity}
                              </Badge>
                              {(incident.investigating_count ?? 0) > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  {incident.investigating_count} investigating
                                </Badge>
                              )}
                            </div>
                          </div>
                        ) : (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{getIncidentTypeLabel(incident.type)}</div>
                            {(incident.source_type === 'pagerduty' || incident.has_pagerduty_signals) && (
                              <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                PagerDuty
                              </Badge>
                            )}
                            {incident.transient && (
                              <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                                <Clock className="h-3 w-3 mr-1" />
                                Transient
                              </Badge>
                            )}
                            {incident.category === 'application' && (
                              <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                                <Code2 className="h-3 w-3 mr-1" />
                                App
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityBadgeVariant(incident.severity)}>
                              {incident.severity}
                            </Badge>
                            {incident.has_rca_result && incident.yaml_fix_count > 0 && (
                              <Badge variant="outline" className="text-xs">
                                <FileWarning className="h-3 w-3 mr-1" />
                                {incident.yaml_fix_count} YAML fix{incident.yaml_fix_count !== 1 ? 'es' : ''}
                              </Badge>
                            )}
                            {incident.has_rca_result && incident.code_fix_count && incident.code_fix_count > 0 && (
                              <Badge variant="outline" className="text-xs">
                                <Code2 className="h-3 w-3 mr-1" />
                                {incident.code_fix_count} code fix{incident.code_fix_count !== 1 ? 'es' : ''}
                              </Badge>
                            )}
                            {incident.github_pr_url && (
                              <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20">
                                <Github className="h-3 w-3 mr-1" />
                                PR #{incident.github_pr_number}
                              </Badge>
                            )}
                          </div>
                        </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Server className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-mono text-sm">{incident.primary_resource}</div>
                              <div className="text-xs text-muted-foreground">{incident.primary_resource_kind}</div>
                            </div>
                          </div>
                          {incident.namespace_owner_emails && incident.namespace_owner_emails.length > 0 && (
                            <TooltipProvider>
                              <div className="flex items-center -space-x-2">
                                {incident.namespace_owner_emails.map((email, idx) => (
                                  <Tooltip key={email}>
                                    <TooltipTrigger asChild>
                                      <div
                                        className="relative w-7 h-7 rounded-full flex items-center justify-center text-white font-semibold text-xs bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-background shadow-sm cursor-help"
                                        style={{ zIndex: incident.namespace_owner_emails!.length - idx }}
                                      >
                                        {getInitials(email)}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">Namespace Owner: {email}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{incident.cluster_name}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusBadgeVariant(incident.status)}>
                            {getStatusLabel(incident.status)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span title={formatDateTime(incident.detected_at)}>
                            {formatTimeAgo(incident.detected_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (incident.is_cluster_group) {
                              navigate(`/incidents/cluster/${incident.cluster_id}`, {
                                state: { clusterName: incident.cluster_name, hideTransient }
                              });
                            } else {
                              navigate(`/incidents/${incident.id}`);
                            }
                          }}
                        >
                          {incident.is_cluster_group ? 'View Incidents' : 'View Details'}
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && paginatedIncidents.length > 0 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFilteredCount)} of {totalFilteredCount} incidents
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

